// This program has been developed by students from the bachelor Computer Science
// at Utrecht University within the Software Project course. Copyright (c) Utrecht
// University (Department of Information and Computing Sciences)

import { spriteMapping } from "@/utils/Types";
import Theme from "@/utils/world/Theme";
import * as PIXI from "pixi.js";

//Base class for a world
export default abstract class World {
  abstract theme: Theme;

  //Renders the world to the pixi instance
  abstract render(): void;
  /**
   * Select the correct theme
   * @param theme Name of the theme
   */
  abstract selectTheme(them: string): Theme;
  //Load the assets in the pixi loader
  abstract loadTheme(): void;

  /**
   * Load sprites with a mapping
   * @param width Amount of sprites in the width
   * @param height Amount of sprites in the height
   * @param map Mapping of where the sprites are located
   * @param sheet The spritesheet
   * @returns Sprites
   */
  protected loadMappedSprite(
    width: number,
    height: number,
    map: spriteMapping[],
    sheet: PIXI.BaseTexture
  ): Record<string | number, PIXI.Texture> {
    const res: Record<string | number, PIXI.Texture> = {};

    //Calculate the dimensions of each sprite
    const w = Math.floor(sheet.width / width);
    const h = Math.floor(sheet.height / height);

    const mask = new PIXI.Rectangle(0, 0, w, h);

    for (let i = 0; i < map.length; i++) {
      //Reposition mask
      mask.x = map[i].x * w;
      mask.y = map[i].y * h;

      //Cut texture
      const texture = new PIXI.Texture(sheet, mask);

      res[map[i].id] = texture;
    }

    return res;
  }
  /**
   * Load sprites that don't have a mapping
   * @param width Amount of sprites in the width
   * @param height Amount of sprites in the height
   * @param sheet The spritesheet
   * @returns Sprites
   */
  protected loadUnmappedSprites(
    width: number,
    height: number,
    sheet: PIXI.BaseTexture
  ): PIXI.Texture[] {
    const res: PIXI.Texture[] = [];

    const w = Math.floor(sheet.width / width);
    const h = Math.floor(sheet.height / height);

    const mask = new PIXI.Rectangle(0, 0, w, h);

    for (let x = 0; x < width; x++) {
      for (let y = 0; y < height; y++) {
        //Reposition mask
        mask.x = x * w;
        mask.y = y * w;

        //Cut texture
        const texture = new PIXI.Texture(sheet, mask);

        res.push(texture);
      }
    }

    return res;
  }
  /**
   * Make an isometric tanslation for 2D grid values for only the positions
   * @param canvas The gameview element of the website
   * @param size The width and height of one grid tile
   * @param point The point to translate
   * @returns Translation
   */
  getIsometricPosition(
    canvas: HTMLCanvasElement,
    size: number,
    point: { x: number; y: number }
  ): { x: number; y: number } {
    const isoX = (point.x - point.y) * size * 0.5 + (canvas.width - size) / 2;
    const isoY =
      point.x * size * 0.25 +
      point.y * size * 0.25 +
      (canvas.height - size) / 4;

    return { x: isoX, y: isoY };
  }
  /**
   * Make a 2D tanslation for isometric grid values for only the positions
   * @param canvas The gameview element of the website
   * @param size The width and height of one grid tile
   * @param point The point to translate
   * @returns Translation
   */
  get2dPosition(
    canvas: HTMLCanvasElement,
    size: number,
    point: { isoX: number; isoY: number }
  ): { x: number; y: number } {
    const y =
      (4 * point.isoY - canvas.height + size) / (2 * size) -
      (2 * point.isoX - canvas.width + size) / (2 * size);
    const x = y + (2 * point.isoX - canvas.width + size) / size;

    return { x: x, y: y };
  }
}
